/* KEEPING THE KEYBOARD INSIDE A MODAL.
 *
 * Seven dialogs in the console declare aria-modal="true" and none of them traps
 * focus. What that means in practice: a DM who opens the importer and presses
 * Tab walks out of it into the page behind, keeps tabbing through a tracker they
 * cannot see, and lands on a button that does something. A screen reader is told
 * the rest of the page does not exist while the Tab key insists it does.
 *
 * The DOM work is small; the decisions are what is worth testing, so they live
 * here as pure functions:
 *
 *   - which element Tab should reach next, including the wrap at either end
 *   - which elements count as focusable at all
 *
 * The hook that reads the DOM and listens for keys is in components/FocusTrap.tsx
 * and calls these.
 */

/** The selector for things a keyboard can reach. Deliberately explicit rather
 *  than clever: [tabindex] alone would include tabindex="-1", which is reachable
 *  by script but NOT by Tab, and including it makes the trap send the keyboard to
 *  something that looks stuck. */
export const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  "details > summary",
].join(", ");

/** Where Tab should land.
 *
 *  `from` is the index of whatever has focus now, or -1 when focus is outside the
 *  dialog entirely -- which happens, and is exactly when a trap earns its keep:
 *  Tab then goes to the first thing inside rather than onwards through the page.
 *
 *  Returns null when there is nothing to focus, so the caller leaves the key
 *  alone instead of swallowing it. A dialog with no focusable content is a
 *  dialog the DM must still be able to Tab out of. */
export function nextIndex(count: number, from: number, back: boolean): number | null {
  if (count <= 0) return null;
  if (from < 0) return back ? count - 1 : 0;
  if (back) return from === 0 ? count - 1 : from - 1;
  return from === count - 1 ? 0 : from + 1;
}

/** Which element should take focus when a dialog opens.
 *
 *  An explicit autofocus wins; then the first text field, because a dialog with
 *  one is almost always asking to be typed into (the palette, the importer, the
 *  rename); then the first focusable thing at all.
 *
 *  Deliberately NOT the close button, which is where "focus the first element"
 *  lands in half the dialogs in this console and means the first keystroke after
 *  opening something can dismiss it. */
export function openFocus<T extends { tagName: string; hasAttribute: (n: string) => boolean; type?: string }>(items: T[]): T | null {
  if (!items.length) return null;
  const autofocus = items.find((el) => el.hasAttribute("autofocus"));
  if (autofocus) return autofocus;
  const typed = items.find((el) => {
    const tag = String(el.tagName).toUpperCase();
    if (tag === "TEXTAREA") return true;
    if (tag !== "INPUT") return false;
    const type = String(el.type || "text").toLowerCase();
    return ["text", "search", "number", "email", "url", "tel", "password", ""].includes(type);
  });
  return typed || items[0];
}

/** Is this element the one that should be returned to when the dialog closes?
 *
 *  Only when it is still in the document and still focusable. A DM who opened a
 *  dialog from a row that the dialog then removed must not have focus thrown at a
 *  detached node, where it silently lands on the body and the next Tab starts
 *  from the top of the page. */
export function canRestore(el: { isConnected?: boolean; offsetParent?: unknown; matches?: (s: string) => boolean } | null): boolean {
  if (!el) return false;
  if (el.isConnected === false) return false;
  try { if (el.matches && !el.matches(FOCUSABLE)) return false; } catch { return false; }
  return true;
}
